import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addQueryReason,
  deleteQueryReason,
  fetchQueryReasonSettings,
  renameQueryReason,
} from "@api";
import { Button, Input } from "@components";
import { SectionHeader, SectionTitle, SectionTagline } from "../components";
import { errorMessageFor } from "./queryReasonSettingsForm";

const REASON_SETTINGS_QUERY_KEY = ["queries", "reason-settings"];

function Queries() {
  const queryClient = useQueryClient();
  const [editingIndex, setEditingIndex] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [actionError, setActionError] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: REASON_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => fetchQueryReasonSettings({ signal }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: REASON_SETTINGS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["queries", "reasons"] });
  };

  const onError = (error) => setActionError(errorMessageFor(error));

  const addMutation = useMutation({
    mutationFn: (label) => addQueryReason(label),
    onSuccess: () => {
      setIsAdding(false);
      setNewLabel("");
      invalidate();
    },
    onError,
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, label }) => renameQueryReason(id, label),
    onSuccess: () => {
      setEditingIndex(null);
      invalidate();
    },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteQueryReason(id),
    onSuccess: invalidate,
    onError,
  });

  const reasons = data ?? [];
  const busy =
    addMutation.isPending || renameMutation.isPending || deleteMutation.isPending;

  const startEdit = (index, reason) => {
    setActionError(null);
    setIsAdding(false);
    setEditingIndex(index);
    setDraftLabel(reason.label);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setDraftLabel("");
  };

  const saveEdit = (reason) => {
    const label = draftLabel.trim();
    if (!label) return;
    renameMutation.mutate({ id: reason.id, label });
  };

  const startAdd = () => {
    setActionError(null);
    setEditingIndex(null);
    setNewLabel("");
    setIsAdding(true);
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setNewLabel("");
  };

  const saveAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    addMutation.mutate(label);
  };

  const cellClass = "px-5 py-3 align-middle text-sm";
  const linkActions = "flex items-center justify-end gap-3";

  return (
    <div className="flex flex-col items-center py-4 px-5 border-b border-zinc-200">
      <div className="py-8 w-full max-w-4xl">
        <SectionHeader>
          <SectionTitle>Query reasons</SectionTitle>
          <SectionTagline>
            When a query is raised on an invoice, these are the reasons that can
            be chosen. Edit the built-in reasons or add your own.
          </SectionTagline>
        </SectionHeader>

        <div className="py-6 w-full">
          {isLoading && (
            <p className="py-6 text-center text-sm text-zinc-400">Loading…</p>
          )}

          {isError && !isLoading && (
            <div className="flex flex-col items-start gap-3 py-6">
              <p className="text-sm text-zinc-500">
                Something went wrong loading your query reasons.
              </p>
              <Button color="secondary" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              <div className="w-full overflow-hidden rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)]">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className={`${cellClass} text-left font-semibold text-zinc-900`}>
                        Reason
                      </th>
                      <th className={`${cellClass} w-40`} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {reasons.map((reason, index) => {
                      const isEditing = editingIndex === index;
                      return (
                        <tr key={reason.id ?? index}>
                          <td className={cellClass}>
                            {isEditing ? (
                              <Input
                                value={draftLabel}
                                onChange={(e) => setDraftLabel(e.target.value)}
                                aria-label="Query reason"
                                className="w-full"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveEdit(reason);
                                  } else if (e.key === "Escape") {
                                    cancelEdit();
                                  }
                                }}
                              />
                            ) : (
                              <span className="text-zinc-900">{reason.label}</span>
                            )}
                          </td>
                          <td className={cellClass}>
                            <div className={linkActions}>
                              {isEditing ? (
                                <>
                                  <Button
                                    color="link"
                                    onClick={() => saveEdit(reason)}
                                    disabled={!draftLabel.trim() || busy}
                                  >
                                    Save
                                  </Button>
                                  <Button color="link" onClick={cancelEdit} disabled={busy}>
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    color="link"
                                    onClick={() => startEdit(index, reason)}
                                    disabled={busy}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    color="link"
                                    onClick={() => deleteMutation.mutate(reason.id)}
                                    disabled={busy}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {isAdding && (
                      <tr>
                        <td className={cellClass}>
                          <Input
                            value={newLabel}
                            onChange={(e) => setNewLabel(e.target.value)}
                            placeholder="New reason"
                            aria-label="New query reason"
                            className="w-full"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                saveAdd();
                              } else if (e.key === "Escape") {
                                cancelAdd();
                              }
                            }}
                          />
                        </td>
                        <td className={cellClass}>
                          <div className={linkActions}>
                            <Button
                              color="link"
                              onClick={saveAdd}
                              disabled={!newLabel.trim() || busy}
                            >
                              Save
                            </Button>
                            <Button color="link" onClick={cancelAdd} disabled={busy}>
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {reasons.length === 0 && !isAdding && (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-5 py-8 text-center text-sm text-zinc-400"
                        >
                          No query reasons yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {!isAdding && (
                  <button
                    type="button"
                    onClick={startAdd}
                    disabled={busy}
                    className="w-full border-t border-zinc-200 px-5 py-3 text-left text-sm font-medium text-primary-600 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    + Add a reason
                  </button>
                )}
              </div>

              {actionError && (
                <p className="mt-3 text-sm text-red-500">{actionError}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Queries;
